import React from 'react'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Attachments from 'proposals/attachments/Attachments'
import { useTranslation } from 'react-i18next'
import useCreateProposalDueDateIcs from './useCreateProposalDueDateIcs'
import Box from '@mui/material/Box'
import { useLocation } from 'react-router-dom'

type RequestForProposalBasicInfoPropTypes = {
  data: RequestForProposalType
}

const RequestForProposalBasicInfo: React.FC<RequestForProposalBasicInfoPropTypes> = ({ data }) => {
  const { t } = useTranslation()
  const location = useLocation()

  const { createProposalDueDateIcs } = useCreateProposalDueDateIcs()

  const isPublicRoute = location.pathname.startsWith('/public/')

  const handleCreateIcs = () => {
    const title = `${t('proposal-due-date-is')}`
    const description = `${t('request-for-proposals')} ${data.title} ${t('at-target')} ${data.target.address}, ${
      data.target.postalCode
    } ${data.target.city}. ${t('due-date-is-2')} ${data.proposalDueDateDisplay}. ${t('proposal-can-be-submitted-at')} ${
      window.location.href
    }`

    createProposalDueDateIcs(data.proposalDueDateDisplayDate, title, description, data.proposalDueDateDisplayTime)
  }

  return (
    <Box>
      {data.buyer && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('buyer')}
          </Typography>
          <Typography>{`${data.buyer.name ?? ''} ${
            data.buyer.businessId ? `(${data.buyer.businessId})` : ''
          }`}</Typography>
          <Typography>{data.buyer.address1 ?? ''}</Typography>
          <Typography>{`${data.buyer.postalCode ?? ''} ${data.buyer.city ?? ''}`}</Typography>
        </Box>
      )}
      {data.buyerContact && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('buyer-contact')}
          </Typography>
          <Typography>{`${data.buyerContact.name ?? ''} ${
            data.buyerContact.businessId ? `(${data.buyerContact.businessId})` : ''
          }`}</Typography>
          {data.buyerContact.address1 && <Typography>{data.buyerContact.address1}</Typography>}
          {(data.buyerContact.postalCode || data.buyerContact.city) && (
            <Typography>{`${data.buyerContact.postalCode ?? ''} ${data.buyerContact.city ?? ''}`}</Typography>
          )}
        </Box>
      )}
      {data.target && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('target')}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end'
            }}
          >
            <div>
              <Typography>{data.target.address ?? '-'}</Typography>
              <Typography>{`${data.target.postalCode ?? '-'} ${data.target.city ?? '-'}`}</Typography>
            </div>
            {data.target.lat && (
              <Box
                sx={{
                  marginLeft: 3
                }}
              >
                <Button
                  variant="outlined"
                  color="grey"
                  component="a"
                  target="_blank"
                  href={`http://maps.google.com/maps?&z={17}&q=${data.target.lat}+${data.target.long}`}
                >
                  {t('map')}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {data.targetDescription && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('target-description')}
          </Typography>

          <Typography
            sx={{
              whiteSpace: 'pre-line'
            }}
          >
            {data.targetDescription}
          </Typography>
        </Box>
      )}
      {data.description && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('request-for-proposal-content')}
          </Typography>

          <Typography
            sx={{
              whiteSpace: 'pre-line'
            }}
          >
            {data.description ? data.description : '-'}
          </Typography>
        </Box>
      )}
      <Attachments contextToken={data.contextToken} resourceWebLinks={data.resourceWebLinks} />
      {data.expectedCompletionDate && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('time-of-execution')}
          </Typography>

          <Typography>{data.expectedCompletionDate}</Typography>
        </Box>
      )}
      {data.proposalDueDate && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Box
            sx={{
              display: 'flex'
            }}
          >
            <div>
              <Typography
                sx={{
                  lineHeight: 1.2
                }}
                variant="body2"
                color="textSecondary"
                component="h6"
              >
                {t('proposal-due-date')}
              </Typography>
              <Typography>{data.proposalDueDateDisplay}</Typography>
            </div>
            {!isPublicRoute && (
              <Box
                sx={{
                  marginLeft: 2
                }}
              >
                <Button color="grey" variant="outlined" onClick={handleCreateIcs}>
                  {t('download-calendar-reminder')}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}
      <Box
        component="article"
        sx={{
          '&:not(:first-of-type)': {
            marginTop: 3
          }
        }}
      >
        <Typography>
          {data.proposalViewingLockedUntilDueDate ? t('request-for-proposal-proposal-viewing-locked') : ''}
        </Typography>
      </Box>
      {data.decisionProcessDescription && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2,
              whiteSpace: 'pre-line'
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('proposal-decision-description')}
          </Typography>

          <Typography
            sx={{
              whiteSpace: 'pre-line'
            }}
          >
            {data.decisionProcessDescription}
          </Typography>
        </Box>
      )}
      {data.contractTerms[0] && (
        <Box
          component="article"
          sx={{
            '&:not(:first-of-type)': {
              marginTop: 3
            }
          }}
        >
          <Typography
            sx={{
              lineHeight: 1.2
            }}
            variant="body2"
            color="textSecondary"
            component="h6"
          >
            {t('contract-term')}
          </Typography>

          <Typography
            sx={{
              whiteSpace: 'pre-line'
            }}
          >
            {data.contractTerms[0].name}
          </Typography>
        </Box>
      )}
      {(data.targetPresentation || data.contactPersons.length > 0) && (
        <Box
          sx={{
            marginTop: 3
          }}
        >
          {data.targetPresentation && (
            <Box
              component="article"
              sx={{
                '&:not(:first-of-type)': {
                  marginTop: 3
                }
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1.2
                }}
                variant="body2"
                color="textSecondary"
                component="h6"
              >
                {t('target-presentation-description')}
              </Typography>

              <Typography
                sx={{
                  whiteSpace: 'pre-line'
                }}
              >
                {data.targetPresentation ? data.targetPresentation : '-'}
              </Typography>
            </Box>
          )}
          {data.contactPersons.length > 0 && (
            <Box
              component="article"
              sx={{
                '&:not(:first-of-type)': {
                  marginTop: 3
                }
              }}
            >
              <Typography
                sx={{
                  lineHeight: 1.2
                }}
                variant="body2"
                color="textSecondary"
                component="h6"
              >
                {t('buyers-representative')}
              </Typography>
              <div>
                {data.contactPersons.map((person) => (
                  <Box
                    sx={{
                      '&:not(:first-of-type)': {
                        marginTop: 2
                      }
                    }}
                    key={person.id}
                  >
                    <Typography>{`${person.firstName} ${person.lastName}`}</Typography>
                    <Typography>{person.roleDescription}</Typography>
                    <Typography>{person.companyName}</Typography>
                    <Typography>{person.email}</Typography>
                    <Typography>{person.phone}</Typography>
                  </Box>
                ))}
              </div>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default RequestForProposalBasicInfo
